import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { UserType } from "@app/enum/UserType";
import { IAccountUserMapping, IUser } from "@app/interfaces/IUser";
import { AccountUserMappingService } from "@app/services/accountUserMapping.service";
import { DataCacheService } from "@app/services/caching/data-cache.service";
import { combineLatest, forkJoin, Observable, of } from "rxjs";
import { map, mergeMap, tap } from "rxjs/operators";
import { IUserIdToNameMapping } from "../foundation.interfaces";
import { IUserResellerMapping } from "../_models/IUserResellerMapping";
import { FoundationUserResellerMappingService } from "./userResellerMapping.service";

export interface IUserQueryOptions {
    Id?: number;
    AccountId?: number;
    JobId?: number;
    JobBookingId?: number;
    Email?: string;
    Type?: UserType;
}

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private usersCacheName: string = "foundationUsersCache";

    constructor(
        private readonly http: HttpClient,
        private readonly accountUserMappingService: AccountUserMappingService,
        private readonly dataCacheService: DataCacheService,
        private readonly userResellerMappingService: FoundationUserResellerMappingService,
    ) {
    }

    getIdToNameMappings(): Observable<IUserIdToNameMapping[]> {
        return this.getUsernameMappings();
    }

    getAllUsers(invalidateCache = false): Observable<IUser[]> {
        return this.dataCacheService.getOrRequest(this.usersCacheName, () => this.getAllUsersFromDb(), { invalidateCache });
    }

    getAllUsersFromDb(): Observable<IUser[]> {
        return this.http.get<IUser[]>(`/api/foundation/users`);
    }

    getUsers(queryOptions: IUserQueryOptions = {}): Observable<IUser[]> {
        return combineLatest([
            this.http.get<IUser[]>(`/api/foundation/users`, { params: queryOptions as HttpParams }),
            this.userResellerMappingService.getMappings(),
            this.accountUserMappingService.getAccountUserMappings()
        ]).pipe(map(([users, userResellerMappings, accountMappings]) => {
            const usersWithMappings = this.applyMappingsToUsers(users, accountMappings, userResellerMappings);
            return usersWithMappings;
        }));
    }

    getUsersForAccount(accountId: number): Observable<IUser[]> {
        return forkJoin({
            accountMappings: this.accountUserMappingService.getAccountUserMappingsForAccount(accountId),
            userResellerMappings: this.userResellerMappingService.getMappings(),
            users: this.http.get<IUser[]>(`/api/foundation/users/account/${accountId}`)
        }).pipe(map(data => {
            const users = this.applyMappingsToUsers(data.users, data.accountMappings, data.userResellerMappings, true, accountId);
            users.forEach(user => {
                user.AccountUserMappings = data.accountMappings.filter(mapping => mapping.UserId == user.Id);
                user.CurrentAccountUserMapping = user.AccountUserMappings.find(m => m.AccountId == accountId);
            });

            return users;
        }))
    }

    currentUserCanUpdateAccountStatus(): Observable<boolean> {
        return this.http.get<boolean>(`/api/foundation/users/updateaccountstatus`);
    }

    saveUser(user: IUser): Observable<number> {
        const userLanguage = localStorage.getItem('userSelectedLanguageCode');
        return this.http.post<number>(`/api/users/`, user, { params: { userLanguage } }).pipe(tap(userId => {
            user.Id = userId;
        }));
    }

    saveUserWithAccountMapping(user: IUser): Observable<IAccountUserMapping> {
        if (user.Id > 0) {
            return this.accountUserMappingService.postAccountUserMapping(user.Email, user.CurrentAccountUserMapping);
        }

        return this.saveUser(user).pipe(mergeMap(userId => {
            return this.accountUserMappingService.postAccountUserMapping(user.Email, user.CurrentAccountUserMapping);
        }));
    }

    deactivateUser(user: IUser): Observable<any> {
        return this.http.post<any>(`/api/users/deactivate`, user);
    }

    toggleInternalUserStatus(user: IUser): Observable<any> {
        if (user.Type == UserType.Engineer || user.Type == UserType.InternalUser) {
            return of(null);
        }

        user.IsActive = !user.IsActive;
        return this.saveUser(user);
    }

    emailExists(emailAddress: string): Observable<boolean> {
        return this.http.get<boolean>(`/api/users/emailexists`, { params: { emailAddress: emailAddress } });
    }


    //--

    private applyMappingsToUsers(users: IUser[], accountMappings: IAccountUserMapping[], userResellerMappings: IUserResellerMapping[], includeCurrent: boolean = false, accountId?: number): IUser[] {
        users.forEach(user => {
            user.AccountUserMappings = accountMappings.filter(mapping => mapping.UserId == user.Id);
            if (includeCurrent && accountId) {
                user.CurrentAccountUserMapping = user.AccountUserMappings.find(m => m.AccountId == accountId);
            }
            user.ResellerIds = userResellerMappings.filter(mapping => mapping.UserId === user.Id && !mapping.EndTime).map(mapping => mapping.ResellerId);
        });

        return users;
    }

    private getUsernameMappings(): Observable<IUserIdToNameMapping[]> {
        return this.http.get<IUserIdToNameMapping[]>(`/api/foundation/users/usernamemappings`);
    }

}
